import axios from 'axios';
import Swiper, { Navigation, Pagination, Autoplay } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/dist/ScrollToPlugin';
window.gsap = gsap;
gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(ScrollToPlugin);

/**
 * Header, nav, footer
 */

// change header style once scroll position has moved from start
const header = document.querySelector('.header');
const body = document.querySelector('body');
let headerStickyPos = body.classList.contains('has-header-banner') ? 50 : 1;
headerSticky();

window.addEventListener('scroll', (event) => {
	headerSticky();
});

function headerSticky() {
	if (document.documentElement.scrollTop > headerStickyPos || document.body.scrollTop > headerStickyPos) {
		header.classList.add('header--sticky');
		body.classList.add('header-is-sticky');
	} else {
		header.classList.remove('header--sticky');
		body.classList.remove('header-is-sticky');
	}
}

// set header style when viewport is inside element with selected class
// first item in the array is the class to check for and the second item is the class to add to the header
// these styles are then overridden when headerSticky() is called
const headerStyles = [
	['.is-light-header', 'header--light'],
	['.is-light-dark-header', 'header--light-dark'],
];
for (let i = 0; i < headerStyles.length; i++) {
	if (document.querySelector(headerStyles[i][0])) {
		ScrollTrigger.create({
			trigger: headerStyles[i][0],
			start: 'top-=100px top',
			end: 'bottom top',
			invalidateOnRefresh: true,
			onEnter: () => {
				if (!header.classList.contains(headerStyles[i][1])) {
					header.classList.add(headerStyles[i][1]);
				}
			},
			onEnterBack: () => {
				if (!header.classList.contains(headerStyles[i][1])) {
					header.classList.add(headerStyles[i][1]);
				}
			},
			onLeave: () => {
				if (header.classList.contains(headerStyles[i][1])) {
					header.classList.remove(headerStyles[i][1]);
				}
			},
		});
	}
}

// toggle nav and burger animation on click
const burger = document.querySelector('.burger');
let navTl;
let headerNotStickyTl;
let headerStickyTl;
if (burger) {
	// create burger animation
	let burgerTl = gsap.timeline({ paused: true }).to('.burger__icon-line', { scaleX: 0, duration: 0.3 }).to('.burger__icon-line:nth-child(1)', { rotate: 45, x: 3, y: -5, duration: 0.1 }).to('.burger__icon-line:nth-child(2)', { rotate: -45, x: -3, y: -5, duration: 0.1 }, '<').to('.burger__icon-line:nth-child(1)', { scaleX: 1, duration: 0.25 }).to('.burger__icon-line:nth-child(2)', { scaleX: 1, duration: 0.25 });

	// set styles for nav columns in prep for animation
	gsap.set('.nav__left-col', { yPercent: -100 });
	gsap.set('.nav__menu', { yPercent: 100 });

	// create nav animation
	navTl = gsap.timeline({ paused: true, onStart: navTlStart, onReverseComplete: navTlRevComplete }).to('.header, .header__bg, .logo-text-bottom', { transition: 'none', duration: 0 }).to('.header__left, .header__right .search-form, .header-nav-right', { autoAlpha: 0, y: -80, duration: 0.25 }, 0.2).to('.header-nav-left', { autoAlpha: 0, duration: 0 }).call(navOn).to('.nav', { autoAlpha: 1, duration: 0 }, '>').to('.nav__left-col, .nav__menu', { yPercent: 0, ease: 'sine.out' }, '>+=0.1').to('.header__left, .header__right .search-form, .header-nav-right', { autoAlpha: 1, y: 0, duration: 0.3 }, '>-=0.1').to('.logo-text-right', { autoAlpha: 1, x: -10, duration: 0.3 }, '<+=0.1');

	// create animation to add to navTl if header is not sticky
	headerNotStickyTl = gsap.timeline({ paused: true }).to('.logo-text-bottom', { autoAlpha: 0, y: -10, duration: 0.25 });
	navTl.add(headerNotStickyTl.play(), 0.1);

	// create animation to add to navTl if header is sticky
	headerStickyTl = gsap.timeline({ paused: true }).to('.logo-text-bottom', { autoAlpha: 0, duration: 0 }).to('.header', { boxShadow: '0px 0px 0px 0px rgba(255,255,255,0)', duration: 0.1 }).to('.header__bg', { yPercent: -100, duration: 0.4 }, '<').to('.header', { height: '140px', duration: 0.8 }, '<');
	navTl.add(headerStickyTl.play(), 0.2);

	burger.addEventListener('click', (event) => {
		if (navTl.progress() == 1) {
			navTl.reverse();
			burgerTl.reverse();
		} else {
			navTl.play();
			burgerTl.play();
		}
	});
}

// disables all header class modifier styles when called, .header--sticky, .header--light etc..
function navOn() {
	header.classList.toggle('header--nav-is-on');
}

function navTlStart() {
	// add active class to body to stop scrolling
	document.querySelector('body').classList.add('nav-is-active');

	// updates navTl with appropriate animation depending on whether header is sticky or not
	if (header.classList.contains('header--sticky')) {
		navTl.remove(headerNotStickyTl);
		navTl.add(headerStickyTl.play(), 0.2);
	} else {
		navTl.remove(headerStickyTl);
		navTl.add(headerNotStickyTl.play(), 0.1);
	}
}

// removes inline styles added by navTl after '.nav' is closed, returning '.header' styles to original state
function navTlRevComplete() {
	// remove active class from body to re-enable scrolling
	document.querySelector('body').classList.remove('nav-is-active');

	header.removeAttribute('style');
	document.querySelector('.header__bg').removeAttribute('style');
	document.querySelector('.logo-text-bottom').removeAttribute('style');
	document.querySelector('.header__left').removeAttribute('style');
	document.querySelector('.search-btn').removeAttribute('style');
	document.querySelector('.header-nav-right').removeAttribute('style');
}

// mobile only nav sub menu
const subMenuToggle = document.querySelectorAll('.primary-nav-menu .menu-item-has-children');
subMenuToggle.forEach((menuItem) => {
	// create overview link and add to sub menu
	let overviewList = document.createElement('li');
	overviewList.className = 'menu-item';
	let overviewLink = menuItem.children[0].cloneNode(true);
	overviewLink.textContent = 'Overview';
	menuItem.children[1].insertBefore(overviewList, menuItem.children[1].querySelector('.menu-item:nth-child(1)'));
	overviewList.appendChild(overviewLink);
	// sub menu toggle animation on click
	menuItem.children[0].addEventListener('click', (event) => {
		if (isMobile) {
			event.preventDefault();
			if (event.target.nextElementSibling.classList.contains('active')) {
				menuItem.classList.remove('active');
				gsapSlideUp(menuItem.children[1]);
			} else {
				subMenuToggle.forEach((menuItem) => {
					if (menuItem.children[1].classList.contains('active')) {
						menuItem.classList.remove('active');
						gsapSlideUp(menuItem.children[1]);
					}
				});
				event.target.offsetParent.classList.add('active');
				gsapSlideDown(event.target.nextElementSibling);
			}
		}
	});
});

// header nav sub menu
const subMenuParent = document.querySelectorAll('.header-nav-left .menu-item-has-children');
let subMenuDelay = false;
let seconds = 0;

subMenuParent.forEach((menu) => {
	menu.addEventListener('mouseenter', (event) => {
		header.classList.add('header--submenu-is-on');
		subMenuDelay = setInterval(function() {
			seconds = seconds + 0.25;
			if (seconds == 0.25) {
				menu.classList.add('active');
				clearInterval(subMenuDelay);
				seconds = 0;
			}
		}, 250);
	});

	menu.addEventListener('mouseleave', (event) => {
		clearInterval(subMenuDelay);
		seconds = 0;
		header.classList.remove('header--submenu-is-on');
		menu.classList.remove('active');
	});
});

// search toggle
const searchToggle = document.querySelector('.search-toggle');
const searchClose = document.querySelector('.search-close');

if (searchToggle) {
	searchToggle.addEventListener('click', (event) => {
		event.preventDefault();
		document.querySelector('.header__right').classList.add('search-is-active');
	});
}

if (searchClose) {
	searchClose.addEventListener('click', (event) => {
		event.preventDefault();
		document.querySelector('.header__right').classList.remove('search-is-active');
	});
}

// newsletterform vars
const newsletterForm = document.querySelector('#newsletter-form');
const newsletterFormWrap = document.querySelector('#newsletter-form-wrapper');
// ajax loader
let newsletterLoader = document.createElement('div');
newsletterLoader.className = 'lds-ripple';
newsletterLoader.appendChild(document.createElement('div'));
newsletterLoader.appendChild(document.createElement('div'));
// form submit
newsletterForm.addEventListener('submit', (event) => {
	event.preventDefault();
	let formData = new FormData(newsletterForm);
	newsletterForm.classList.add('ajaxing');
	newsletterForm.append(newsletterLoader);
	formData.append('action', 'newsletter_form');
	// form ajax
	axios
		.post(ajaxurl, formData)
		.then((response) => {
			// console.log(response);
			// remove ajax and loader
			newsletterForm.classList.remove('ajaxing');
			newsletterLoader.remove();
			// form response and status vars
			let formMessage = newsletterFormWrap.querySelector('.form-response');
			formMessage.innerHTML = '';
			let formStatus;
			if (response.data !== '' && response.data.constructor === Object) {
				formStatus = response.data.status;
			} else {
				formStatus = response.data;
			}
			// get response message based on status
			let responseMessage = newsletterFormWrap.querySelector('.template-' + formStatus).content.cloneNode(true);
			if (formStatus == 'success') {
				newsletterFormWrap.innerHTML = '';
				newsletterFormWrap.append(responseMessage);
				newsletterFormWrap.classList.add('newsletter-form-success');
				// send GA conversion
				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push({
					event: 'formSubmission',
					formType: 'Newsletter',
				});
			} else {
				formMessage.append(responseMessage);
				formMessage.classList.add('form-response--' + formStatus);
				response.data.errors.forEach((error) => {
					let list = document.createElement('li');
					list.textContent = error;
					newsletterFormWrap.querySelector('#newsletter-form-errors').append(list);
				});
			}
		})
		.catch((error) => {
			console.log(error);
			newsletterForm.classList.remove('ajaxing');
			newsletterLoader.remove();
		});
});

/**
 * Blocks
 */

// cal block
const calBlock = document.querySelector('.cal-block');
const calBlockBtn = document.querySelector('.cal-block-btn');
const calBlockCloseBtn = document.querySelector('.cal-block-close-btn');
const calBlockPlayer = document.querySelector('.cal-block__player');
let calClosed = sessionStorage.getItem('calClosed');
// console.log(calClosed);

if (calBlock && calClosed != 'true') {
	const calBlockTimer = calBlock.getAttribute('data-popup-timer');
	setTimeout(() => {
		calBlockBtn.classList.add('active');
		calBlock.classList.add('active');
		calBlockPlayer.currentTime = 0;
		setTimeout(() => {
			calBlockPlayer.play();
		}, '500');
	}, calBlockTimer);
}

if (calBlockBtn) {
	calBlockBtn.addEventListener('click', (event) => {
		event.preventDefault();
		calBlockBtn.classList.add('active');
		calBlock.classList.add('active');
		calBlockPlayer.currentTime = 0;
		setTimeout(() => {
			calBlockPlayer.play();
		}, '500');
	});
}

if (calBlockCloseBtn) {
	calBlockCloseBtn.addEventListener('click', (event) => {
		event.preventDefault();
		sessionStorage.setItem('calClosed', 'true');
		calBlockBtn.classList.remove('active');
		calBlock.classList.remove('active');
		setTimeout(() => {
			calBlockPlayer.pause();
		}, '500');
	});
}

// sticky navigation
const stickyNavigation = document.querySelector('.sticky-navigation:not(.sticky-navigation--fixed)');
if (stickyNavigation) {
	stickyNav();

	window.addEventListener('scroll', (event) => {
		stickyNav();
	});
}

function stickyNav() {
	let stickyNavigationPos = stickyNavigation.querySelector('.sticky-navigation__wrapper').offsetTop - document.querySelector('.header').offsetHeight;
	if (document.documentElement.scrollTop > stickyNavigationPos || document.body.scrollTop > stickyNavigationPos) {
		stickyNavigation.classList.add('is-sticky');
		header.classList.add('has-sticky-sub-nav');
	} else {
		stickyNavigation.classList.remove('is-sticky');
		header.classList.remove('has-sticky-sub-nav');
	}
}

const stickyNavigationDropdown = document.querySelector('.sticky-navigation');
if (stickyNavigationDropdown) {
	stickyNavigationDropdown.querySelector('.sticky-menu-current').addEventListener('click', (event) => {
		event.preventDefault();
		stickyNavigationDropdown.querySelector('.menu').classList.toggle('active');
		if (stickyNavigationDropdown.querySelector('.menu').classList.contains('active')) {
			stickyNavigationDropdown.querySelector('.sticky-menu-current').setAttribute('aria-expanded', 'true');
		} else {
			stickyNavigationDropdown.querySelector('.sticky-menu-current').setAttribute('aria-expanded', 'false');
		}
	});
}

// sticky dropdown menu navigation
const stickyNavigationParentMenuItems = document.querySelectorAll('.sticky-navigation .menu-item-has-children');
if (stickyNavigationParentMenuItems) {
	stickyNavigationParentMenuItems.forEach((item) => {
		let itemLink = item.querySelector('a');
		let itemSubMenu = item.querySelector('.sub-menu');
		if (!window.matchMedia('(pointer: coarse)').matches) {
			item.addEventListener('mouseenter', () => {
				subMenuShow(item, itemLink, itemSubMenu);
			});
			item.addEventListener('mouseleave', () => {
				subMenuhide(item, itemLink, itemSubMenu);
			});
		} else {
			itemLink.addEventListener('click', (event) => {
				event.preventDefault();
				if (itemSubMenu.classList.contains('active')) {
					subMenuhide(item, itemLink, itemSubMenu);
				} else {
					subMenuShow(item, itemLink, itemSubMenu);
				}
			});
		}
		itemSubMenu.querySelector('.menu-item:last-child > a').addEventListener('blur', (event) => {
			if (event.relatedTarget == null || !event.relatedTarget.parentNode.parentNode.classList.contains('sub-menu')) {
				subMenuhide(item, itemLink, itemSubMenu);
			}
		});
		itemLink.addEventListener('focusout', (event) => {
			if (event.relatedTarget == null || !event.relatedTarget.parentNode.parentNode.classList.contains('sub-menu')) {
				subMenuhide(item, itemLink, itemSubMenu);
			}
		});
	});
}

function subMenuShow(menuItemEl, menuItemLinkEl, subMenuEl, isMobile) {
	menuItemEl.classList.add('active');
	menuItemLinkEl.setAttribute('aria-expanded', 'true');
	subMenuEl.classList.add('active');
	subMenuEl.style.opacity = '1';
	subMenuEl.style.visibility = 'visible';
	subMenuEl.style.pointerEvents = 'all';
}

function subMenuhide(menuItemEl, menuItemLinkEl, subMenuEl, isMobile) {
	menuItemEl.classList.remove('active');
	menuItemLinkEl.setAttribute('aria-expanded', 'false');
	subMenuEl.classList.remove('active');
	subMenuEl.style.opacity = '0';
	subMenuEl.style.visibility = 'hidden';
	subMenuEl.style.pointerEvents = 'none';
}

// tab cards
const tabCards = document.querySelector('.tab-cards');
if (tabCards) {
	let tabs = tabCards.querySelectorAll('.tab-cards__tab');
	let cards = tabCards.querySelectorAll('.tab-cards__card');
	tabs.forEach((tab, index) => {
		tab.addEventListener('click', (event) => {
			event.preventDefault();
			if (!event.target.classList.contains('active')) {
				tabCards.querySelector('.tab-cards__tab.active').classList.remove('active');
				tabCards.querySelector('.tab-cards__card.active').classList.remove('active');
				event.target.classList.add('active');
				cards[index].classList.add('active');
			}
		});
	});
}

// intro textarea reveal toggle
const introToggle = document.querySelector('.intro__toggle');
if (introToggle) {
	introToggle.addEventListener('click', (event) => {
		event.preventDefault();
		if (event.target.classList.contains('active')) {
			event.target.classList.remove('active');
			gsapSlideUp(event.target.nextElementSibling);
		} else {
			event.target.classList.add('active');
			gsapSlideDown(event.target.nextElementSibling);
		}
	});
}

// switch content reveal toggle
const switchToggle = document.querySelector('.switch-toggle');
if (switchToggle) {
	switchToggle.querySelector('input[type="checkbox"]').addEventListener('change', (event) => {
		if (switchToggle.querySelector('input[type="checkbox"]').checked) {
			switchToggle.classList.add('active');
			document.querySelector('.switch-content:nth-child(1)').classList.remove('active');
			document.querySelector('.switch-content:nth-child(2)').classList.add('active');
		} else {
			switchToggle.classList.remove('active');
			document.querySelector('.switch-content:nth-child(2)').classList.remove('active');
			document.querySelector('.switch-content:nth-child(1)').classList.add('active');
		}
	});
}

// accordion reveal toggle
const accordionToggle = document.querySelectorAll('.accordion');
if (accordionToggle) {
	accordionToggle.forEach((accordion) => {
		let accordionItems = accordion.querySelectorAll('.accordion__item');
		accordionItems.forEach((item) => {
			item.querySelector('.accordion__trigger').addEventListener('click', (event) => {
				event.preventDefault();
				accordionReveal(event, item, accordionItems);
			});
		});
	});
}

function accordionReveal(event, item, accordionItems) {
	if (event.target.classList.contains('active')) {
		event.target.classList.remove('active');
		gsapSlideUp(event.target.nextElementSibling);
	} else {
		accordionItems.forEach((item) => {
			if (item.querySelector('.accordion__trigger').classList.contains('active')) {
				item.querySelector('.accordion__trigger').classList.remove('active');
				gsapSlideUp(item.querySelector('.accordion__trigger').nextElementSibling);
			}
		});
		event.target.classList.add('active');
		gsapSlideDown(event.target.nextElementSibling);
	}
}

// partners list autoplay slider
let partners = gsap.utils.toArray('.partners-logos--slider');
partners.forEach((partner) => {
	let partnersTrack = partner.querySelector('.partners-logos__track');
	let partnersDuration = partner.querySelectorAll('.partners-logos__item').length * 4;
	let partnerClone = partner.querySelector('.partners-logos__list').cloneNode(true);
	partnersTrack.appendChild(partnerClone);

	let partnersLogosTl = gsap
		.timeline({
			onReverseComplete: () => {
				partnersLogosTl.reverse(0);
			},
			onComplete: () => {
				partnersLogosTl.restart();
			},
		})
		.from(partner.querySelectorAll('.partners-logos__list'), { xPercent: -100, duration: partnersDuration, ease: 'none' })
		.reverse(0);

	ScrollTrigger.observe({
		target: partnersTrack,
		type: 'pointer,touch',
		onDragStart: (self) => {
			partnersLogosTl.pause();
		},
		onDragEnd: (self) => {
			self.deltaX > 0 ? partnersLogosTl.play() : partnersLogosTl.reverse();
		},
		dragMinimum: 10,
	});
});

// testimonials slider
if (document.querySelector('.testimonials__slider')) {
	const testimonialSlider = new Swiper('.testimonials__slider .swiper', {
		modules: [Navigation],
		autoHeight: true,
		navigation: {
			nextEl: '.testimonials__slider-nav .swiper__next',
			prevEl: '.testimonials__slider-nav .swiper__prev',
		},
	});
}

// card slider block
if (document.querySelector('.card-slider-block__slider')) {
	const cardSliderBlock = new Swiper('.card-slider-block__slider .swiper', {
		modules: [Navigation, Pagination],
		loop: true,
		loopedSlides: 2,
		navigation: {
			nextEl: '.card-slider-block__slider-arrows .swiper__next',
			prevEl: '.card-slider-block__slider-arrows .swiper__prev',
		},
		pagination: {
			el: document.querySelector('.card-slider-block__slider').querySelector('.swiper-pagination'),
			type: 'progressbar',
		},
	});
}

// image text slider block
if (document.querySelector('.img-text-slider-block')) {
	const imgTextSliderBlock = new Swiper('.img-text-slider-block__slider .swiper', {
		modules: [Navigation, Pagination],
		autoHeight: true,
		spaceBetween: 110,
		threshold: 10,
		navigation: {
			nextEl: '.img-text-slider-block__slider-arrows .swiper__next',
			prevEl: '.img-text-slider-block__slider-arrows .swiper__prev',
		},
		pagination: {
			el: document.querySelector('.img-text-slider-block__slider').querySelector('.swiper-pagination'),
			type: 'progressbar',
		},
	});
	const imgTextSliderBlockFilter = document.querySelector('.img-text-slider-block__slider-filter');
	if (imgTextSliderBlockFilter) {
		// desktop filter
		// init filter
		let swiperInitFilter = imgTextSliderBlockFilter.querySelector('.swiper__filter.active');
		swiperFilter(imgTextSliderBlock, swiperInitFilter);
		// filter on click
		imgTextSliderBlockFilter.querySelectorAll('.swiper__filter').forEach((filter) => {
			filter.addEventListener('click', (event) => {
				event.preventDefault();
				swiperFilter(imgTextSliderBlock, filter);
				imgTextSliderBlockFilter.querySelector('.swiper__filter.active').classList.remove('active');
				filter.classList.add('active');
			});
		});
		// mobile filter
		// open/close dropdown filter
		let swiperFilterDropdown = imgTextSliderBlockFilter.querySelector('.swiper__filter-dropdown');
		swiperFilterDropdown.querySelector('.swiper__filter-dropdown-current').addEventListener('click', (event) => {
			event.preventDefault();
			swiperFilterDropdown.classList.toggle('active');
		});
		// filter on click
		swiperFilterDropdown.querySelectorAll('.swiper__filter-dropdown-option').forEach((filter) => {
			filter.addEventListener('click', (event) => {
				event.preventDefault();
				swiperFilter(imgTextSliderBlock, filter);
				swiperFilterDropdown.querySelector('.swiper__filter-dropdown-current span').textContent = filter.textContent;
				swiperFilterDropdown.classList.remove('active');
			});
		});
	}
}

function swiperFilter(swiper, filter) {
	// check for hidden slides and add back into slider if they match selected filter
	let swiperHiddenSlides = swiper.wrapperEl.querySelectorAll('.non-swiper-slide');
	if (swiperHiddenSlides) {
		swiperHiddenSlides.forEach((slide) => {
			if (slide.getAttribute('data-solution') == filter.getAttribute('data-filter')) {
				slide.classList.remove('non-swiper-slide');
				slide.classList.add('swiper-slide');
				slide.style.display = 'initial';
			}
		});
	}
	// hide slides that don't match selected filter
	swiper.slides.forEach((slide) => {
		if (slide.getAttribute('data-solution') != filter.getAttribute('data-filter')) {
			slide.classList.remove('swiper-slide');
			slide.classList.add('non-swiper-slide');
			slide.style.display = 'none';
		}
	});
	swiper.update();
	swiper.slideTo(0);
}

// testimonials block slider
if (document.querySelector('.testimonial-block__slider')) {
	const testimonialBlockSlider = new Swiper('.testimonial-block__slider .swiper', {
		modules: [Navigation, Pagination],
		autoHeight: true,
		spaceBetween: 50,
		navigation: {
			nextEl: '.testimonial-block__slider-nav .swiper__next',
			prevEl: '.testimonial-block__slider-nav .swiper__prev',
		},
		pagination: {
			el: document.querySelector('.testimonial-block__slider-nav').querySelector('.swiper-pagination'),
			type: 'progressbar',
		},
	});
}

// stats block
const statsBlock = document.querySelectorAll('.stats-block');
let statsBlockSlider = [];
let statsBlockSliderInit = [];
for (let i = 0; i < statsBlock.length; i++) {
	statsBlockSliderInit[i] = false;
}
function statsBlockSliderMode() {
	if (statsBlock) {
		statsBlock.forEach((slider, index) => {
			let statsBlockSliderQuery = window.matchMedia('(max-width: 559px)');

			if (statsBlockSliderQuery.matches && !statsBlockSliderInit[index]) {
				statsBlockSliderInit[index] = true;
				statsBlockSlider[index] = new Swiper(slider.querySelector('.swiper'), {
					modules: [Navigation],
					autoHeight: true,
					spaceBetween: 30,
					navigation: {
						nextEl: slider.querySelector('.stats-block__slider-nav .swiper__next'),
						prevEl: slider.querySelector('.stats-block__slider-nav .swiper__prev'),
					},
				});
			} else if (!statsBlockSliderQuery.matches && statsBlockSliderInit[index]) {
				statsBlockSlider[index].destroy();
				statsBlockSliderInit[index] = false;
			}
		});
	}
}
statsBlockSliderMode(); // also called in resize function, see below

// stats grid block slider
const statsGridBlock = document.querySelectorAll('.stats-grid-block__slider');
let statsGridBlockSlider = [];
let statsGridBlockSliderInit = [];
for (let i = 0; i < statsGridBlock.length; i++) {
	statsGridBlockSliderInit[i] = false;
}
function statsGridBlockSliderMode() {
	if (statsGridBlock) {
		statsGridBlock.forEach((slider, index) => {
			let statsGridBlockSliderQuery = window.matchMedia('(max-width: 599px)');

			if (statsGridBlockSliderQuery.matches && !statsGridBlockSliderInit[index]) {
				statsGridBlockSliderInit[index] = true;
				statsGridBlockSlider[index] = new Swiper(slider.querySelector('.swiper'), {
					modules: [Navigation],
					autoHeight: true,
					spaceBetween: 30,
					navigation: {
						nextEl: slider.nextSibling.nextSibling.querySelector('.swiper__next'),
						prevEl: slider.nextSibling.nextSibling.querySelector('.swiper__prev'),
					},
				});
			} else if (!statsGridBlockSliderQuery.matches && statsGridBlockSliderInit[index]) {
				statsGridBlockSlider[index].destroy();
				statsGridBlockSliderInit[index] = false;
			}
		});
	}
}
statsGridBlockSliderMode(); // also called in resize function, see below

// contact form block vars
const contactFormBlock = document.querySelector('#contact-form-block-form');
const contactFormBlockWrap = document.querySelector('#contact-form-block-form-wrapper');
if (contactFormBlock) {
	// ajax loader
	let contactFormBlockLoader = document.createElement('div');
	contactFormBlockLoader.className = 'lds-ripple';
	contactFormBlockLoader.appendChild(document.createElement('div'));
	contactFormBlockLoader.appendChild(document.createElement('div'));
	// form submit
	contactFormBlock.addEventListener('submit', (event) => {
		event.preventDefault();
		let formData = new FormData(contactFormBlock);
		contactFormBlock.classList.add('ajaxing');
		contactFormBlock.append(contactFormBlockLoader);
		formData.append('action', 'contact_form_block');
		// form ajax
		axios
			.post(ajaxurl, formData)
			.then((response) => {
				// console.log(response);
				// remove ajax and loader
				contactFormBlock.classList.remove('ajaxing');
				contactFormBlockLoader.remove();
				// form response and status vars
				let formMessage = contactFormBlockWrap.querySelector('.form-response');
				formMessage.innerHTML = '';
				let formStatus;
				if (response.data !== '' && response.data.constructor === Object) {
					formStatus = response.data.status;
				} else {
					formStatus = response.data;
				}
				// get response message based on status
				let responseMessage = contactFormBlockWrap.querySelector('.template-' + formStatus).content.cloneNode(true);
				if (formStatus == 'success') {
					contactFormBlockWrap.innerHTML = '';
					contactFormBlockWrap.append(responseMessage);
					contactFormBlockWrap.classList.add('contact-form-block-success');
					// send GA conversion
					window.dataLayer = window.dataLayer || [];
					window.dataLayer.push({
						event: 'formSubmission',
						formType: formData.get('form_name'),
					});
				} else {
					formMessage.append(responseMessage);
					formMessage.classList.add('form-response--' + formStatus);
					response.data.errors.forEach((error) => {
						let list = document.createElement('li');
						list.textContent = error;
						contactFormBlockWrap.querySelector('#contact-form-block-errors').append(list);
					});
				}
				// scroll response into view
				if (document.querySelector('.contact-form-block')) {
					gsap.to(window, { delay: 0.2, duration: 0.7, scrollTo: { y: '.contact-form-block', offsetY: 100 }, ease: 'sine.out' });
				}
			})
			.catch((error) => {
				console.log(error);
				contactFormBlock.classList.remove('ajaxing');
				contactFormBlockLoader.remove();
			});
	});
}

// box cards slider block
if (document.querySelector('.box-cards-slider-block')) {
	const boxCardsSliderBlock = new Swiper('.box-cards-slider-block .swiper', {
		modules: [Navigation, Pagination, Autoplay],
		autoHeight: true,
		slidesPerView: 1,
		spaceBetween: 20,
		autoplay: {
			delay: 3000,
			disableOnInteraction: false,
		},
		navigation: {
			nextEl: '.box-cards-slider-block__cards-nav .swiper__next',
			prevEl: '.box-cards-slider-block__cards-nav .swiper__prev',
		},
		pagination: {
			el: document.querySelector('.box-cards-slider-block__cards-nav').querySelector('.swiper-pagination'),
			type: 'progressbar',
		},
		breakpoints: {
			480: {
				slidesPerView: 1,
				spaceBetween: 30,
			},
			768: {
				slidesPerView: 2,
				spaceBetween: 40,
			},
			1280: {
				slidesPerView: 3,
				spaceBetween: 40,
			}
		},
	});
	boxCardsSliderBlock.autoplay.stop();
	if (window.matchMedia('(min-width: 1280px)').matches && boxCardsSliderBlock.slides.length === 3) {
		boxCardsSliderBlock.el.classList.add('swiper-all-visible');
	}
	ScrollTrigger.create({
		trigger: '.box-cards-slider-block .swiper',
		start: 'top bottom',
		end: 'bottom top',
		invalidateOnRefresh: true,
		// markers: true,
		onEnter: () => {
			boxCardsSliderBlock.autoplay.start();
		},
		onLeave: () => {
			boxCardsSliderBlock.autoplay.stop();
		},
		onEnterBack: () => {
			boxCardsSliderBlock.autoplay.start();
		},
		onLeaveBack: () => {
			boxCardsSliderBlock.autoplay.stop();
		}
	});
}

// service cards block
if (document.querySelector('.service-cards-block')) {
	const serviceCardsBlockSlider = new Swiper('.service-cards-block__slider .swiper', {
		modules: [Navigation, Pagination],
		autoHeight: true,
		slidesPerView: 1,
		spaceBetween: 30,
		navigation: {
			nextEl: '.service-cards-block__slider-nav .swiper__next',
			prevEl: '.service-cards-block__slider-nav .swiper__prev',
		},
		pagination: {
			el: document.querySelector('.service-cards-block__slider').querySelector('.swiper-pagination'),
			type: 'progressbar',
		},
		breakpoints: {
			600: {
				slidesPerView: 2,
				spaceBetween: 30,
			},
			768: {
				slidesPerView: 2,
				spaceBetween: 40,
			},
			1280: {
				slidesPerView: 3,
				spaceBetween: 40,
			},
			1360: {
				slidesPerView: 4,
				spaceBetween: 40,
			}
		}
	});

	// service cards filter setup
	const serviceCardsFilterDropdown = document.querySelector('.service-cards-block .filter-dropdown');
	let serviceCardsCurrentFilter = serviceCardsFilterDropdown.querySelector('.filter-dropdown__current');
	let serviceCardsCurrentFilterSlug = serviceCardsCurrentFilter.getAttribute('data-filter');
	let serviceCardsFilterItems = serviceCardsFilterDropdown.querySelectorAll('.menu-item');
	// add selected class
	serviceCardsFilterDropdown.querySelector(`.menu-item[data-filter="${serviceCardsCurrentFilterSlug}"]`).classList.add('selected');
	// open menu and close menu if click outside filter el
	serviceCardsCurrentFilter.addEventListener('click', (event) => {
		serviceCardsFilterDropdown.classList.add('active');
	});
	let serviceCardsFilterListener = function(event) {
		closeFilter(event, serviceCardsFilterDropdown, serviceCardsFilterListener);
	}
	document.addEventListener('click', serviceCardsFilterListener, true);
	// init filter
	swiperCatFilter(serviceCardsBlockSlider, serviceCardsCurrentFilter);
	// update dropdown with new selected filter and close menu
	serviceCardsFilterItems.forEach((item) => {
		item.addEventListener('click', (event) => {
			let newFilterSlug = item.getAttribute('data-filter');
			let newFilterName = item.textContent;
			serviceCardsCurrentFilter.setAttribute('data-filter', newFilterSlug);
			serviceCardsCurrentFilter.querySelector('span').textContent = newFilterName;
			serviceCardsFilterDropdown.querySelector('.menu-item.selected').classList.remove('selected');
			item.classList.add('selected');
			serviceCardsFilterDropdown.classList.remove('active');
			// filter service cards
			swiperCatFilter(serviceCardsBlockSlider, item);
		});
	});
}

// swiper filter by category
function swiperCatFilter(swiper, filter) {
	// check for hidden slides and add back into slider if they match selected filter
	let swiperHiddenSlides = swiper.wrapperEl.querySelectorAll('.non-swiper-slide');
	if (swiperHiddenSlides) {
		swiperHiddenSlides.forEach((slide) => {
			if (slide.getAttribute('data-category') == filter.getAttribute('data-filter')) {
				slide.classList.remove('non-swiper-slide');
				slide.classList.add('swiper-slide');
				slide.style.display = 'initial';
			}
		});
	}
	// hide slides that don't match selected filter
	swiper.slides.forEach((slide) => {
		if (slide.getAttribute('data-category') != filter.getAttribute('data-filter')) {
			slide.classList.remove('swiper-slide');
			slide.classList.add('non-swiper-slide');
			slide.style.display = 'none';
		}
	});
	swiper.update();
	swiper.slideTo(0);
}

// close filter dropdown when clicking outside
function closeFilter(event, filter, filterListener) {
	let targetEl = event.target;
	do {
		if (targetEl == filter) {
			// console.log('Clicked inside!');
			return;
		}
		// Go up the DOM
		targetEl = targetEl.parentNode;
	}
	while (targetEl);
	// console.log('Clicked outside!');
	filter.classList.remove('active');
	// document.removeEventListener('click', filterListener, true);
}

// animation to reveal display none content
function gsapSlideDown(el) {
	el.style.display = 'block';
	let getHeight = el.offsetHeight;
	el.removeAttribute('style');
	gsap.set(el, { display: 'block', height: 0, overflow: 'hidden' });
	gsap.to(el, { height: getHeight, clearProps: 'height,overflow' });
	el.classList.add('active');
}

// animation to hide content to display none
function gsapSlideUp(el) {
	gsap.to(el, { height: 0, overflow: 'hidden', clearProps: 'height,overflow' });
	gsap.to(el, { display: 'none' });
	el.classList.remove('active');
}

// use matchMedia to check if viewport is in mobile size
let isMobile;
if (window.matchMedia('(max-width: 479px)').matches) {
	isMobile = true;
} else if (window.matchMedia('(min-width: 480px)').matches) {
	isMobile = false;
}

// update isMobile on resize, only updates when viewport moves above or below matchMedia query
function isMobileResize() {
	if (window.matchMedia('(max-width: 479px)').matches && isMobile == false) {
		isMobile = true;
	} else if (window.matchMedia('(min-width: 480px)').matches && isMobile == true) {
		// remove inline styles added by sub menu toggle animation
		subMenuToggle.forEach((menuItem) => {
			menuItem.children[1].removeAttribute('style');
			menuItem.children[1].classList.remove('active');
		});
		isMobile = false;
	}
}

// resize function
window.addEventListener('resize', (event) => {
	isMobileResize();

	statsBlockSliderMode();

	statsGridBlockSliderMode();
});

// youtube video block
const ytVideoBlock = document.querySelector('.yt-video');
if (ytVideoBlock) {
	var tag = document.createElement('script');
	tag.id = 'yt-iframe-api';
	tag.src = 'https://www.youtube.com/iframe_api';
	var firstScriptTag = document.getElementsByTagName('script')[0];
	firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
}

// Change file input text with uploaded file name
const fileInputs = document.querySelectorAll('.file-input');
fileInputs.forEach((input) => {
	let label = input.nextElementSibling,
	labelVal = label.innerHTML;
	input.addEventListener('change', function(e) {
		let fileName = '';
		if (this.files && this.files.length > 1) {
			fileName = (this.getAttribute( 'data-multiple-caption' ) || '' ).replace( '{count}', this.files.length);
		} else {
			fileName = e.target.value.split('\\').pop();
		}
		if (fileName) {
			label.querySelector('span').innerHTML = fileName;
		} else {
			label.innerHTML = labelVal;
		}
	});
});

// animate scroll to section
const scrollToLinks = document.querySelectorAll('.scrollto');
if (scrollToLinks) {
	scrollToLinks.forEach((link) => {
		link.addEventListener('click', function (e) {
			e.preventDefault();
			let link_href = link.getAttribute('href');
			let el = document.querySelector(link_href);
			window.scroll({
				behavior: 'smooth',
				left: 0,
				top: el.offsetTop - 200,
			});
		});
	});
}

/**
 * GSAP animations
 */

// cursor change
const customCursorBlocks = document.querySelectorAll('.has-custom-cursor');
if (customCursorBlocks && window.matchMedia('(hover: hover)').matches) {
	customCursorBlocks.forEach((block) => {
		let cursorPointer = document.createElement('div');

		cursorPointer.className = 'custom-cursor';

		if (block.classList.contains('has-custom-cursor--drag')) {
			cursorPointer.textContent = 'Drag';
		}
		if (block.classList.contains('has-custom-cursor--watch')) {
			cursorPointer.textContent = 'Watch Video';
		}
		window.addEventListener('pointermove', (event) => {
			moveCursor(event, cursorPointer);
		});
		block.addEventListener('mouseenter', (event) => {
			cursorOn(cursorPointer);
		});
		block.addEventListener('mouseover', (event) => {
			block.appendChild(cursorPointer);
		});
		block.addEventListener('mouseout', (event) => {
			cursorPointer.remove();
		});

		let innerLinks = block.querySelectorAll('.btn');
		innerLinks.forEach((link) => {
			link.addEventListener('mouseenter', (event) => {
				cursorPointer.remove();
			});
			link.addEventListener('mouseout', (event) => {
				cursorOn(cursorPointer);
			});
		});
	});
}

function moveCursor(event, cursor) {
	gsap.to(cursor, {
		x: event.clientX,
		y: event.clientY,
		duration: 0,
	});
}

function cursorOn(cursor) {
	gsap.set(cursor, { scale: 0, autoAlpha: 0 });
	gsap.to(cursor, {
		scale: 1,
		autoAlpha: 1,
		duration: 0.7,
		ease: 'power2.out',
	});
}

// circle heading anim
const stickyFadeOut = document.querySelector('.sticky-fade-out');
if (stickyFadeOut) {
	let stickyFadeOutTl = gsap.timeline({
		scrollTrigger: {
			trigger: stickyFadeOut,
			start: 'center center',
			end: '=+500',
			pin: true,
			pinSpacing: false,
			scrub: 1,
			invalidateOnRefresh: true,
			// markers: true,
		},
	});
	stickyFadeOutTl.to(stickyFadeOut, { autoAlpha: 0, ease: 'power2.out' });
}

// cards anim
const cardsAnim = document.querySelector('.cards-anim');
if (cardsAnim) {
	const cardsAnimItems = gsap.utils.toArray('.cards-anim .cards__item');
	let cardsAnimMm = gsap.matchMedia();
	cardsAnimMm.add('(min-width: 1024px)', () => {
		cardsAnimItems.forEach((card, index) => {
			let tl = gsap.timeline({
				scrollTrigger: {
					trigger: cardsAnim,
					start: 'top bottom',
					end: 'bottom 75%',
					scrub: 1,
					invalidateOnRefresh: true,
					// markers: true,
				},
			});
			if ((index + 1) % 2 === 0) {
				tl.to(card, { y: -75, ease: 'power2.out' });
			} else {
				tl.to(card, { y: -150, ease: 'power1.out' });
			}
		});
	});
}

const cards3ColAnim = document.querySelector('.cards-3-col-anim');
if (cards3ColAnim) {
	const cards3ColAnimItems = document.querySelectorAll('.cards-3-col-anim .cards__item:nth-child(3n), .cards-3-col-anim .cards__item:nth-child(3n+1)');
	let cards3ColAnimmMm = gsap.matchMedia();
	cards3ColAnimmMm.add('(min-width: 1360px)', () => {
		cards3ColAnimItems.forEach((card, index) => {
			let tl = gsap.timeline({
				scrollTrigger: {
					trigger: cards3ColAnim,
					start: 'top bottom',
					end: 'bottom 75%',
					scrub: 1,
					invalidateOnRefresh: true,
					// markers: true,
				},
			});
			tl.to(card, { y: -75, ease: 'power2.out' });
		});
	});
}

// icon cards anim
const iconsCardsAnim = document.querySelector('.icons-cards-anim');
if (iconsCardsAnim) {
	const iconsCardsAnimItems = gsap.utils.toArray('.icons-cards-anim .icon-cards-block__card');
	let iconsCardsAnimMm = gsap.matchMedia();
	iconsCardsAnimMm.add('(min-width: 1024px)', () => {
		iconsCardsAnimItems.forEach((card, index) => {
			if ((index + 1) % 2 === 0) {
				gsap.set(card, { y: 70, ease: 'power2.out' });
			} else {
				gsap.set(card, { y: 35, ease: 'power1.out' });
			}
			let tl = gsap.timeline({
				scrollTrigger: {
					trigger: iconsCardsAnim,
					start: 'top bottom',
					end: 'bottom 75%',
					scrub: 1,
					invalidateOnRefresh: true,
					// markers: true,
				},
			});
			if ((index + 1) % 2 === 0) {
				tl.to(card, { y: -35, ease: 'power2.out' });
			} else {
				tl.to(card, { y: -105, ease: 'power1.out' });
			}
		});
	});
}

// cards shapes anim
const cardsShapesAnim = document.querySelector('.cards-shapes-anim');
const cardsShapesAnimSm = document.querySelector('.cards-shapes-anim--sm');
if (cardsShapesAnim) {
	const cardsShapesAnimItems = gsap.utils.toArray('.cards-shapes-anim .cards__item');
	cardsShapesAnimItems.forEach((card, index) => {
		if ((index + 1) % 4 == 1) {
			// add shapes to card
			let circlePrimary = document.createElement('div'),
				smCircleTertiary = document.createElement('div'),
				trianglePrimaryLilac = document.createElement('div');
			circlePrimary.className = 'anim-circle-primary';
			smCircleTertiary.className = 'anim-sm-circle-tertiary';
			trianglePrimaryLilac.className = 'anim-triangle-primary-lilac';
			card.appendChild(circlePrimary);
			card.appendChild(smCircleTertiary);
			card.appendChild(trianglePrimaryLilac);
			// animate shapes on scroll
			gsap.timeline({
				scrollTrigger: {
					trigger: card,
					start: 'top 65%',
					end: '100% 65%',
					toggleActions: 'play none none reverse',
					invalidateOnRefresh: true,
					// markers: true,
				},
			})
				.fromTo(smCircleTertiary, { scale: 0 }, { scale: 1, ease: 'power2.out', duration: 1.5 })
				.fromTo(circlePrimary, { scale: 0 }, { scale: 1, ease: 'power2.out', duration: 1.5 }, '<=0.1')
				.fromTo(trianglePrimaryLilac, { x: -30, y: -30 }, { x: 0, y: 0, ease: 'power2.out', duration: 1.5 }, '<=0.2');
		} else if ((index + 1) % 4 == 2) {
			// add shapes to card
			let nestCricleSecondary = document.createElement('div'),
				nestCricleTertiary = document.createElement('div');
			nestCricleSecondary.appendChild(document.createElement('div'));
			nestCricleTertiary.appendChild(document.createElement('div'));
			nestCricleSecondary.className = 'anim-nest-circle-secondary';
			cardsShapesAnimSm && nestCricleSecondary.classList.add('anim-nest-circle-secondary--sm');
			nestCricleTertiary.className = 'anim-nest-circle-tertiary';
			cardsShapesAnimSm && nestCricleTertiary.classList.add('anim-nest-circle-tertiary--sm');
			card.appendChild(nestCricleSecondary);
			card.appendChild(nestCricleTertiary);
			// animate shapes on scroll
			gsap.timeline({
				scrollTrigger: {
					trigger: card,
					start: 'top center',
					end: '60% center',
					toggleActions: 'play none none reverse',
					invalidateOnRefresh: true,
					// markers: true,
				},
			})
				.fromTo(nestCricleTertiary, { scale: 0 }, { scale: 1, ease: 'power2.out', duration: 1.5 })
				.fromTo(nestCricleSecondary, { scale: 0 }, { scale: 1, ease: 'power2.out', duration: 1.5 }, '<=0.1');
		} else if ((index + 1) % 4 == 3) {
			// add shapes to card
			let triangleSecondary = document.createElement('div'),
				smCirclePrimary = document.createElement('div');
			triangleSecondary.className = 'anim-triangle-secondary';
			cardsShapesAnimSm && triangleSecondary.classList.add('anim-triangle-secondary--sm');
			smCirclePrimary.className = 'anim-sm-circle-primary';
			card.appendChild(triangleSecondary);
			card.appendChild(smCirclePrimary);
			// animate shapes on scroll
			gsap.timeline({
				scrollTrigger: {
					trigger: card,
					start: 'top 70%',
					end: '100% 70%',
					toggleActions: 'play none none reverse',
					invalidateOnRefresh: true,
					// markers: true,
				},
			})
				.fromTo(triangleSecondary, { scale: 0, x: 30, y: 30 }, { scale: 1, x: 0, y: 0, ease: 'power2.out', duration: 1.5 })
				.fromTo(smCirclePrimary, { scale: 0 }, { scale: 1, ease: 'power2.out', duration: 1.5 }, '<=0.7');
		} else if ((index + 1) % 4 === 0) {
			// add shapes to card
			let circlePrimarySky = document.createElement('div');
			circlePrimarySky.className = 'anim-circle-primary-sky';
			card.appendChild(circlePrimarySky);
			// animate shapes on scroll
			gsap.timeline({
				scrollTrigger: {
					trigger: card,
					start: 'top 60%',
					end: '60% 60%',
					toggleActions: 'play none none reverse',
					invalidateOnRefresh: true,
					// markers: true,
				},
			}).fromTo(circlePrimarySky, { scale: 0 }, { scale: 1, ease: 'power2.out', duration: 1.5 });
		}
	});
}

// animation for shapes on the hero
const heroShapesAnim = document.querySelector('.hero-shapes-anim');
if (heroShapesAnim) {
	// add shapes to card
	let triangleSecondary = document.createElement('div'),
		circleTertiary = document.createElement('div');
	triangleSecondary.className = 'anim-triangle-secondary anim-triangle-secondary--hero';
	circleTertiary.className = 'anim-circle-tertiary';
	heroShapesAnim.querySelector('.hero__img').appendChild(triangleSecondary);
	heroShapesAnim.querySelector('.hero__img-clip').appendChild(circleTertiary);
	// animate shapes on load
	gsap.timeline().fromTo(triangleSecondary, { scale: 0 }, { scale: 1, ease: 'power2.out', duration: 1.5 }).fromTo(triangleSecondary, { x: 30, y: 30 }, { x: 0, y: 0, ease: 'power2.out', duration: 2 }, 0).fromTo(circleTertiary, { scale: 0 }, { scale: 1, ease: 'power2.out', duration: 1.5 }, '<=0.3');
}

const heroCardsShapesAnim = document.querySelector('.hero-cards-shapes-anim');
if (heroCardsShapesAnim) {
	// add shapes to card
	let triangleSecondary = document.createElement('div');
	triangleSecondary.className = 'anim-triangle-secondary';
	heroCardsShapesAnim.querySelector('.hero-cards__header').appendChild(triangleSecondary);
	// animate shapes on load
	gsap.timeline()
	.fromTo(triangleSecondary, { scale: 0 }, { scale: 1, ease: 'power2.out', duration: 1.5 })
	.fromTo(heroCardsShapesAnim.querySelector('.hero-cards__scroll-down'), { scale: 0 }, { scale: 1, ease: 'power2.out', duration: 1.5 }, 0.2)
	.fromTo(triangleSecondary, { x: 30, y: 30 }, { x: 0, y: 0, ease: 'power2.out', duration: 2 }, 0);
}

// apply now cta trigger
const applyNowCta = document.querySelector('.apply-now-cta');
if (applyNowCta) {
	let applyNowCtaTl = gsap.timeline({
		scrollTrigger: {
			trigger: document.querySelector('.footer-card'),
			start: 'top-=50px 100%',
			end: 'bottom bottom',
			toggleActions: 'play none none reverse',
			invalidateOnRefresh: true,
			// markers: true,
		},
	});
	applyNowCtaTl.to(applyNowCta, { scale: 0.75, y: -30, transformOrigin: 'top', autoAlpha: 0, duration: 0.3, ease: 'power2.out' });
}

// change apply now cta style on dark backgrounds
const darkBlocks = document.querySelectorAll('.is-dark-block');
if (applyNowCta && darkBlocks) {
	darkBlocks.forEach((block) => {
		ScrollTrigger.create({
			trigger: block,
			start: 'top+=98px bottom',
			end: 'bottom bottom-=97px',
			invalidateOnRefresh: true,
			// markers: true,
			onToggle: () => {
				applyNowCta.classList.toggle('apply-now-cta--light');
			},
		});
	});
}

// quick links block
const quickLinksBlock = document.querySelector('.quick-links-block');
if (quickLinksBlock) {
	let quickLinksBlockStart = 'top top+=50px';
	let quickLinksBlockEnd = 'top top+=80px';
	if (quickLinksBlock.classList.contains('quick-links-block--np-top')) {
		quickLinksBlockStart = 'top top+=130px';
	}
	let quickLinksBlockEndTrigger = '#' + quickLinksBlock.querySelector('.quick-links-block__wrapper').getAttribute('data-end-trigger');
	if (quickLinksBlockEndTrigger == '#') {
		quickLinksBlockEndTrigger = '.footer';
	}
	let quickLinksBlockMm = gsap.matchMedia();
	quickLinksBlockMm.add('(min-width: 1024px)', () => {
		ScrollTrigger.create({
			trigger: quickLinksBlock,
			endTrigger: quickLinksBlockEndTrigger,
			pin: quickLinksBlock.querySelector('.quick-links-block__wrapper'),
			pinSpacing: false,
			start: quickLinksBlockStart,
			end: quickLinksBlockEnd,
			invalidateOnRefresh: true,
			// markers: true,
			onToggle: () => {
				quickLinksBlock.classList.toggle('quick-links-block--active');
			},
		});
	});
}

/**
 * ### PAUL
 */

const stratReviewForms = document.querySelectorAll('.strategy-review-form');
if (stratReviewForms) {
	stratReviewForms.forEach((reviewForm) => {
		ScrollTrigger.create({
			trigger: reviewForm,
			start: 'top+=98px bottom',
			end: 'bottom bottom-=97px',
			invalidateOnRefresh: true,
			// markers: true,
			onToggle: () => {
				applyNowCta.classList.toggle('apply-now-cta--opacity');
			},
		});
	});
}

const phone_links = document.querySelectorAll('.phone-links');
phone_links.forEach((phone_link) => {
	phone_link.addEventListener('click', function () {
		phoneTracking();
	});
});

const header_phone_links = document.querySelectorAll('.header .menu-item a');
header_phone_links.forEach((phone_link) => {
	if (phone_link.getAttribute('href').includes('tel:')) {
		phone_link.addEventListener('click', function () {
			phoneTracking();
		});
	}
});

function phoneTracking() {
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push({
		event: 'telephoneCall',
	});
}
